'use client';

import * as React from 'react';

import { Button } from '@/components/atoms/Button';
import { cn } from '@/components/atoms/primitives/utils';
import { ResilienceState, type ResilienceKind } from '@/components/atoms/motion/ResilienceState';
import {
  buildNoMatchEmptyCopy,
  noMatchParamsFromDate,
  type NoMatchEmptyCopy,
} from '@/lib/discover/noMatchEmptyState';
import type { DiscoverParams } from '@/lib/discover/url-state';

/**
 * Empty-state kind drives the body copy, the visible action, and the DS 1.4.0
 * `ResilienceState` icon treatment shown beside the message.
 *
 * - `no-match` (default): request succeeded with zero matching events. Name
 *   the active filters (WEB-939), offer Widen when a strictly wider date
 *   preset exists, and Reset filters.
 * - `request-failed`: request rejected (4xx) or timed out (5xx). Body copy
 *   reads "Couldn't load events." with the quiet retry ring and a Retry
 *   action so the user can re-issue the active query instead of clearing
 *   their filters.
 * - `rate-limited`: request was throttled (429). Body copy reads "Rate
 *   limited. Try again in M:SS." with a live countdown; Retry is **disabled**
 *   until the countdown reaches zero so the user can't immediately re-429
 *   (WEB-289). Once it hits zero the copy switches to the generic
 *   request-failed line and Retry becomes clickable.
 * - `offline`: the device reports no connectivity (`navigator.onLine` is
 *   false) AND a fetch error is present. Heading is "You're offline"; body
 *   is "Reconnect and try again." (WEB-938). Retry re-issues the query.
 */
export type DiscoverEmptyStateKind = 'no-match' | 'request-failed' | 'rate-limited' | 'offline';

/**
 * Map a {@link DiscoverEmptyStateKind} to the visual {@link ResilienceKind}
 * treatment. `no-match` reuses the calm `empty` icon; the retryable failure
 * kinds (`request-failed` / `rate-limited`) share the quiet `retry` ring;
 * `offline` gets the slow rose beat. Kept as a pure lookup so the mapping is
 * greppable and unit-testable.
 */
function toResilienceKind(kind: DiscoverEmptyStateKind): ResilienceKind {
  switch (kind) {
    case 'offline':
      return 'offline';
    case 'request-failed':
    case 'rate-limited':
      return 'retry';
    case 'no-match':
    default:
      return 'empty';
  }
}

export interface DiscoverEmptyStateProps {
  /**
   * Kind of empty state to render. Defaults to `'no-match'` so existing
   * call sites keep their current behaviour (request succeeded, zero rows).
   */
  readonly kind?: DiscoverEmptyStateKind;
  /**
   * Seconds remaining before a rate-limited Retry becomes available. Only
   * consumed when `kind === 'rate-limited'`; ignored otherwise. The component
   * owns the per-second tick internally from this seed value.
   */
  readonly retryAfterSeconds?: number;
  /** Fired when the user clicks "Reset" — caller should clear filters. */
  readonly onReset: () => void;
  /**
   * Fired when the user clicks "Retry" in the `request-failed` /
   * `rate-limited` kinds. Optional — when omitted the Retry button is
   * hidden. Production callers should always pass it; tests may omit it.
   */
  readonly onRetry?: () => void;
  /**
   * Active Discover URL state. Consumed only for `no-match` copy + whether
   * Widen is available (WEB-939). Ignored for error kinds.
   */
  readonly params?: DiscoverParams;
  /**
   * Venue display names keyed by location id. Optional — unnamed ids fall
   * back to `"Venue"`.
   */
  readonly venueNames?: ReadonlyMap<string, string>;
  /**
   * Fired when the user clicks Widen in the `no-match` kind. Optional —
   * when omitted (or when no strictly wider date preset exists) the button
   * is hidden.
   */
  readonly onWiden?: () => void;
  /** Optional class merged onto the outer container. */
  readonly className?: string;
}

const REQUEST_FAILED_MESSAGE = "Couldn't load events. Try again.";
/**
 * v3 DIS-9 body, first sentence only. Design also has "The last list you
 * loaded is still on screen when you go back." — that clause is untrue when
 * this empty state renders (no rows). Deviation recorded for WEB-945.
 */
const OFFLINE_MESSAGE = 'Reconnect and try again.';

/** Countdown tick cadence. One second — extracted so it's not a magic number. */
const COUNTDOWN_TICK_MS = 1000;

/**
 * Format a non-negative second count as `M:SS` (e.g. `217` → `"3:37"`).
 * Minutes are not zero-padded; seconds always are. Negative / non-finite
 * inputs clamp to `0:00` so a bad seed never renders garbage.
 */
export function formatCountdown(totalSeconds: number): string {
  const safe = Number.isFinite(totalSeconds) && totalSeconds > 0 ? Math.floor(totalSeconds) : 0;
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Live one-second countdown. Seeds from `initialSeconds`, ticks down on a
 * fixed interval, and stops at zero. The interval is cleared on unmount and
 * whenever the seed changes (a fresh 429 restarts the clock) — no leaked
 * timers. Returns the current remaining seconds.
 */
function useCountdown(initialSeconds: number): number {
  const safeInitial =
    Number.isFinite(initialSeconds) && initialSeconds > 0 ? Math.floor(initialSeconds) : 0;
  const [remaining, setRemaining] = React.useState(safeInitial);

  // Re-seed when the source value changes (e.g. a new 429 with a different
  // Retry-After). Keying the effect on `safeInitial` restarts the clock.
  React.useEffect(() => {
    setRemaining(safeInitial);
    if (safeInitial <= 0) return;
    const id = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, COUNTDOWN_TICK_MS);
    return () => clearInterval(id);
  }, [safeInitial]);

  return remaining;
}

/**
 * Short heading rendered inside the {@link ResilienceState} visual beside the
 * icon. Kept terse and stable per kind — the longer, action-oriented copy (and
 * the live countdown) lives in the announced `<p>` below the visual. Keeping
 * the two strings distinct avoids visible duplication while letting the icon
 * row carry the DS 1.4.0 motion treatment.
 */
const RESILIENCE_HEADING: Record<DiscoverEmptyStateKind, string> = {
  'no-match': 'No matches',
  'request-failed': "Couldn't load",
  'rate-limited': "Couldn't load",
  offline: "You're offline",
};

function NoMatchMessage({ copy }: { readonly copy: NoMatchEmptyCopy }): React.ReactElement {
  if (copy.mode === 'named') {
    return (
      <>
        No events match{' '}
        {copy.namedLabels.map((label, index) => (
          <React.Fragment key={`${label}:${String(index)}`}>
            {index > 0 ? ' + ' : null}
            <strong>{label}</strong>
          </React.Fragment>
        ))}{' '}
        {copy.scope}.{copy.hint ? ` ${copy.hint}` : null}
      </>
    );
  }
  if (copy.mode === 'count') {
    return (
      <>
        No events match <strong>{copy.filterCount} filters</strong> {copy.scope}.
        {copy.hint ? ` ${copy.hint}` : null}
      </>
    );
  }
  return (
    <>
      {copy.headline}
      {copy.hint ? ` ${copy.hint}` : null}
    </>
  );
}

/**
 * Empty-state slot for `DiscoverListView`. Rendered when either:
 *
 *   - The active filter set returned zero events (`kind='no-match'`,
 *     default),
 *   - The active query was rejected by the backend
 *     (`kind='request-failed'`),
 *   - The active query was rate limited (`kind='rate-limited'`) — shows a
 *     live countdown and disables Retry until it clears, or
 *   - The device is offline with a fetch error present (`kind='offline'`).
 *
 * The icon + kind treatment come from the DS 1.4.0 {@link ResilienceState}
 * motion atom (WEB-468), aria-neutralized so this component owns the single
 * announced live region. Mostly presentational — the parent owns the Reset /
 * Retry / Widen actions so it can clear URL params, re-issue the RTK Query, or
 * widen `date`. The only local state is the rate-limit countdown.
 */
export function DiscoverEmptyState({
  kind = 'no-match',
  retryAfterSeconds = 0,
  onReset,
  onRetry,
  params,
  venueNames,
  onWiden,
  className,
}: DiscoverEmptyStateProps): React.ReactElement {
  const isRateLimited = kind === 'rate-limited';
  const isOffline = kind === 'offline';
  const isNoMatch = kind === 'no-match';
  // The hook always runs (rules of hooks); when not rate-limited the seed is
  // 0 so it's an inert no-op (no interval scheduled).
  const remaining = useCountdown(isRateLimited ? retryAfterSeconds : 0);
  const countdownActive = isRateLimited && remaining > 0;

  const noMatchCopy = React.useMemo(() => {
    if (!isNoMatch) return null;
    return buildNoMatchEmptyCopy(params ?? noMatchParamsFromDate(undefined), {
      venueNames,
    });
  }, [isNoMatch, params, venueNames]);

  let message: React.ReactNode;
  if (countdownActive) {
    message = `Rate limited. Try again in ${formatCountdown(remaining)}.`;
  } else if (isOffline) {
    message = OFFLINE_MESSAGE;
  } else if (kind === 'request-failed' || isRateLimited) {
    // Rate-limit window elapsed → fall through to the generic retryable copy.
    message = REQUEST_FAILED_MESSAGE;
  } else if (noMatchCopy) {
    // Polite status so filter-driven copy is announced when this empty
    // state stays mounted (WEB-939). Do not announce a result count — that
    // lives on DiscoverResultsBar.
    message = (
      <span role="status">
        <NoMatchMessage copy={noMatchCopy} />
      </span>
    );
  } else {
    message = null;
  }

  const isErrorKind = kind === 'request-failed' || isRateLimited || isOffline;
  const showRetry = isErrorKind && onRetry !== undefined;
  const showWiden =
    isNoMatch && onWiden !== undefined && noMatchCopy !== null && noMatchCopy.widenLabel !== null;
  // `role="alert"` is an implicit assertive + atomic live region. Apply it
  // only to the one-shot `request-failed` message. For `rate-limited` the
  // body text changes every second; an assertive ancestor would make screen
  // readers re-announce the whole message on every tick (WEB-289 a11y).
  // `offline` is excluded too — it can flip on/off with connectivity and
  // shouldn't barge in. The message <p> carries its own polite live region
  // when relevant.
  const useAlertRole = kind === 'request-failed';
  // Disable Retry while the rate-limit countdown is still running so the user
  // can't immediately fire another request that just gets re-429'd.
  const retryDisabled = countdownActive;
  const resilienceKind = toResilienceKind(kind);

  return (
    <div
      data-testid="discover-empty-state"
      data-empty-state-kind={kind}
      role={useAlertRole ? 'alert' : undefined}
      className={cn(
        'flex min-h-[40vh] flex-col items-center justify-center gap-4 px-6 py-12 text-center',
        className,
      )}
    >
      {/*
        DS 1.4.0 ResilienceState (WEB-468) supplies the icon + kind treatment
        (pulsing-O / retry ring / rose offline beat). Its own `status` region
        is neutralized (`role={undefined}` + `aria-live="off"`) so this
        component owns the single announced live region via the `<p>` below —
        no nested live regions, no per-tick re-announce. Reduced-motion is
        handled by the atom's CSS (`@media prefers-reduced-motion`).
      */}
      <ResilienceState
        kind={resilienceKind}
        title={RESILIENCE_HEADING[kind]}
        // Empty string (not `null`/`undefined`) so the atom's `detail ??
        // default` does NOT fall back to the per-kind default copy — the
        // announced detail lives in the `<p>` below to avoid duplication.
        detail=""
        role={null}
        ariaLive="off"
        className="max-w-md text-left"
      />
      <p
        className="max-w-md text-sm text-loci-text-muted"
        // Politely announce the ticking countdown to assistive tech without
        // interrupting; the second-by-second change shouldn't barge in.
        aria-live={countdownActive ? 'polite' : undefined}
        data-testid="discover-empty-state-message"
      >
        {message}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {showRetry ? (
          <Button
            type="button"
            variant="accent"
            size="sm"
            onClick={onRetry}
            disabled={retryDisabled}
            aria-disabled={retryDisabled || undefined}
            data-testid="discover-empty-state-retry"
          >
            Retry
          </Button>
        ) : null}
        {showWiden ? (
          <Button
            type="button"
            variant="accent"
            size="sm"
            onClick={onWiden}
            data-testid="discover-empty-state-widen"
          >
            {noMatchCopy?.widenLabel}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onReset}
          data-testid="discover-empty-state-reset"
        >
          {isNoMatch ? 'Reset filters' : 'Reset'}
        </Button>
      </div>
    </div>
  );
}
